import type { MaybeRefOrGetter } from 'vue';
import { computed, isRef, ref, toValue, watch } from 'vue';
import { DEFAULT_LISTING_PAGE_SIZE } from '#layers/ui/constants/pagination';

export interface UsePaginationOptions {
	page?: number; // default is 1
	pageSize?: number;
	totalItems?: number;
}

export function usePagination(
	initialParams?: MaybeRefOrGetter<UsePaginationOptions>,
	totalItems: MaybeRefOrGetter<number> = 0,
) {
	const defaultParams = { page: 1, pageSize: DEFAULT_LISTING_PAGE_SIZE };
	const params = ref<UsePaginationOptions>({
		...defaultParams,
		...(initialParams ? toValue(initialParams) : {}),
	});

	if (isRef(initialParams)) {
		watch(initialParams, (newVal) => {
			params.value = { ...params.value, ...newVal };
		});
	}

	const paginationInfo = computed(() => {
		const total = toValue(totalItems);
		const pageSize = params.value.pageSize || 1;
		const page = params.value.page || 1;
		const totalPages = Math.max(1, Math.ceil(total / pageSize));

		if (page > totalPages && totalPages > 0) {
			params.value.page = totalPages;
		}

		const startIndex = (page - 1) * pageSize;
		const endIndex = Math.min(startIndex + pageSize, total);

		return {
			page,
			pageSize,
			totalItems: total,
			totalPages,
			startIndex,
			endIndex,
		};
	});

	const pagination = computed(() => ({
		page: paginationInfo.value.page,
		pageSize: paginationInfo.value.pageSize,
		totalItems: paginationInfo.value.totalItems,
	}));

	function setPage(page: number) {
		const totalPages = paginationInfo.value.totalPages;
		if (page < 1 || page > totalPages) return;
		params.value = { ...params.value, page };
	}

	function setPageSize(pageSize: number) {
		if (pageSize < 1) return;
		params.value = { ...params.value, pageSize, page: 1 };
	}

	function paginateArray<T>(items: T[]): T[] {
		const { startIndex, endIndex } = paginationInfo.value;
		return items.slice(startIndex, endIndex);
	}

	return {
		pagination,
		setPage,
		setPageSize,
		paginateArray,
	};
}
